'use client';

import { useCallback, useEffect, useRef } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useModal } from 'components/shared/Modal';
import PartnerTypeformModal from 'components/pulsomics/PartnerTypeformModal';
import ButtonV2 from 'components/shared/ButtonV2';

import { scienceLandingAsset } from './asset';
import { useReveal } from './useReveal';
import styles from './EnhanceResearch.module.css';

/* §10 — "Enhance your research on a whole new level" (Figma 1019:3011). A rounded
   (r24) card behind a looping, muted video — a hand wearing the Ring, resting in
   tall grass moving in the wind (a ~20 s ping-pong loop: the clip plays forward, then
   in reverse back to frame one, so the loop point never jumps) — with a soft
   left shade, a left-aligned headline + body and one CTA, "Partner with us",
   which opens the partner Typeform used by the Pulsomics institutions band. */

export default function EnhanceResearch() {
  const t = useTranslations('science');
  // heading, line, CTA stagger in over the video; the video card stays put
  const revealRef = useReveal<HTMLDivElement>();
  const ModalClient = useModal();
  const videoRef = useRef<HTMLVideoElement | null>(null);

  /* play only while on screen; reduced motion keeps the still first frame */
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      v.pause();
      return undefined;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e?.isIntersecting) v.play().catch(() => {});
        else v.pause();
      },
      { threshold: 0.1 },
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  const open = useCallback(
    (source: string) => () => {
      ModalClient.set(<PartnerTypeformModal source={source} />);
      ModalClient.setCloseButtonTheme('dark');
      ModalClient.setPortal(true);
      ModalClient.show();
    },
    [ModalClient],
  );

  return (
    <section className={styles.section} aria-labelledby="science-collaborate-title">
      <div className={styles.card}>
        {/* the video's own first frame, under it, so the card never sits empty
            while the video loads — sized per device by next/image and cropped
            exactly like the video */}
        <Image
          className={styles.image}
          src={scienceLandingAsset('enhance/enhance-research-ascii-poster.jpg')}
          alt=""
          aria-hidden="true"
          fill
          sizes="calc(100vw - 20px)"
        />
        <video
          ref={videoRef}
          className={styles.image}
          aria-label={t('science.landing.collaborate.imageAlt')}
          muted
          loop
          playsInline
          autoPlay
          preload="metadata"
        >
          <source src={scienceLandingAsset('enhance/enhance-research-ascii.mp4')} type="video/mp4" />
        </video>
        <div className={styles.shade} aria-hidden="true" />
        <div ref={revealRef} className={styles.content}>
          <h2 id="science-collaborate-title" className={styles.heading} data-reveal>
            {t('science.landing.collaborate.titleLine1')}
            <br />
            {t('science.landing.collaborate.titleLine2')}
          </h2>
          {/* the full line on desktop, a shorter one on phones */}
          <p className={styles.body} data-reveal>
            <span className={styles.bodyLong}>{t('science.landing.collaborate.body')}</span>
            <span className={styles.bodyShort}>{t('science.landing.collaborate.bodyShort')}</span>
          </p>
          <div className={styles.ctas} data-reveal>
            <ButtonV2
              variant="white"
              aria-haspopup="dialog"
              onClick={open('science-landing-partner')}
              label={t('science.landing.collaborate.partner')}
              className={styles.cta}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
